// apps/web/src/features/crm/leads/list/EngagementListPage.tsx
//
// `/leads` — the v4 manager surface (#2247 FR-1, BR-1/BR-3). A filtered list
// (not a kanban / "my day") over the READ-ONLY Engagement projection: bucket
// chips with server counts, a 1..5 score narrow, a bound-client narrow,
// free-text search, `recent|oldest` sort, and cursor "load more". Reads through
// `useEngagementsList`; every write (create) goes through the legacy
// `LeadCreateModal`. Rows route to `/leads/:leadId` (the Lead UUID, BR-4).
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { EngagementBucket, type EngagementListRow } from '@magimp/shared';
import { useEngagementsList, type EngagementsListFilter } from '@magimp/shared-hooks';
import {
  Button,
  EmptyState,
  ErrorState,
  FilterPanel,
  FilterToggleButton,
  Input,
  LoadingState,
  ToolbarActions,
  useFilterDisclosure,
} from '../../../../ui/index.js';
import { errorMessage } from '../../../../api/errors.js';
import { hasPermission, useAuthStore } from '../../../../stores/auth.js';
import { LeadCreateModal } from '../components/LeadCreateModal.js';
import { EngagementBucketChips } from './EngagementBucketChips.js';
import { EngagementListTable } from './EngagementListTable.js';
import { EngagementListFilters, type ScoreFilter } from './EngagementListFilters.js';

export function EngagementListPage() {
  const { t } = useTranslation(['leads', 'common']);
  const navigate = useNavigate();
  const perms = useAuthStore((s) => s.permissions);
  const canManage = perms === 'ALL' || hasPermission(perms, 'leads.manage');
  const hasAllScope = perms === 'ALL' || hasPermission(perms, 'leads.scope.all');
  const canAssign = perms === 'ALL' || hasPermission(perms, 'leads.assign');

  const [bucket, setBucket] = useState<EngagementBucket>(EngagementBucket.ALL);
  const [sort, setSort] = useState<'recent' | 'oldest'>('recent');
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [score, setScore] = useState<ScoreFilter>('');
  const [client, setClient] = useState<{ id: string; name: string } | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const { open, toggle, panelId } = useFilterDisclosure();

  useEffect(() => {
    const h = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(h);
  }, [q]);

  useEffect(() => {
    if (!hasAllScope && bucket === EngagementBucket.UNASSIGNED) {
      setBucket(EngagementBucket.ALL);
    }
  }, [bucket, hasAllScope]);

  const effectiveBucket =
    !hasAllScope && bucket === EngagementBucket.UNASSIGNED ? EngagementBucket.ALL : bucket;
  const query: EngagementsListFilter = { bucket: effectiveBucket, sort };
  if (debouncedQ) query.q = debouncedQ;
  if (score) query.score = Number(score);
  if (client) query.clientId = client.id;

  // WHY (#2307): opt into the CRM v4 fidelity header so the row carries
  //   `title`/`source`/`hasOpenTask`/`openTaskDueAt`. A query flag is FORBIDDEN
  //   — the strict backend query DTO 400s unknown keys on a stale bundle.
  const listQ = useEngagementsList(query, { fidelity: true, scope: hasAllScope });
  const rows: EngagementListRow[] = useMemo(
    () => (listQ.data?.pages ?? []).flatMap((p) => p.items),
    [listQ.data],
  );
  const counts = listQ.data?.pages[0]?.counts ?? null;
  const filtersActiveCount = (score ? 1 : 0) + (client ? 1 : 0);

  const goToLead = (id: string) => navigate(`/leads/${id}`);
  const onSelect = (row: EngagementListRow) => {
    if (row.leadId) goToLead(row.leadId);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minHeight: 0, flex: 1 }}>
      <header style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>{t('leads:title')}</h1>
          {counts && (
            <div style={{ fontSize: 13, color: 'var(--fg-3)', marginTop: 2 }}>
              {t('leads:list.totalCount', { count: counts.all })}
            </div>
          )}
        </div>
        <ToolbarActions>
          <Input
            icon="search"
            value={q}
            onChange={setQ}
            placeholder={t('leads:filters.search.placeholder')}
            ariaLabel={t('leads:filters.search.label')}
          />
          <FilterToggleButton
            open={open}
            activeCount={filtersActiveCount}
            label={t('leads:list.filtersToggle')}
            aria-controls={panelId}
            onClick={toggle}
            data-testid="engagement-filters-toggle"
          />
          {canManage && (
            <Button icon="plus" kind="primary" onClick={() => setCreateOpen(true)}>
              {t('leads:actions.create')}
            </Button>
          )}
        </ToolbarActions>
      </header>

      {counts && (
        <EngagementBucketChips
          active={effectiveBucket}
          counts={counts}
          onSelect={setBucket}
          showUnassigned={hasAllScope}
        />
      )}

      <FilterPanel open={open} id={panelId}>
        <EngagementListFilters
          score={score}
          onScore={setScore}
          client={client}
          onClientPick={setClient}
          onClientClear={() => setClient(null)}
        />
      </FilterPanel>

      {listQ.error && (
        <ErrorState
          title={t('common:states.loadError')}
          text={errorMessage(listQ.error)}
          onRetry={() => void listQ.refetch()}
        />
      )}
      {listQ.isPending && <LoadingState />}
      {listQ.isSuccess && (
        <>
          <EngagementListTable
            rows={rows}
            onSelect={onSelect}
            sort={sort}
            onSortChange={setSort}
            showAssignee={hasAllScope}
            canAssign={canAssign}
            emptyState={
              <EmptyState title={t('common:states.emptyTitle')} text={t('leads:empty')} />
            }
          />
          {listQ.hasNextPage && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 8 }}>
              <Button
                onClick={() => void listQ.fetchNextPage()}
                disabled={listQ.isFetchingNextPage}
                data-testid="engagement-load-more"
              >
                {listQ.isFetchingNextPage
                  ? t('common:states.loading')
                  : t('leads:actions.loadMore')}
              </Button>
            </div>
          )}
        </>
      )}

      {createOpen && (
        <LeadCreateModal onClose={() => setCreateOpen(false)} onOpenExisting={goToLead} />
      )}
    </div>
  );
}
